import _extends from "@babel/runtime/helpers/esm/extends";
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { doNotForwardTransientProp } from '../../util/doNotForwardTransientProp';
import CustomPropTypes from '../../util/custom-prop-types';
import { deleteUndefined } from '../../util/deleteUndefined';
import Adornment from '../Adornment/Adornment';
import { AdornedInputMixin } from '../../mixins/components/AdornedInputMixin';
import { FormFieldContext } from '../../js/FormFieldContext';
import { identifier } from '../../util/identifier';
export var StyledAdornedInput = /*#__PURE__*/styled('div').withConfig({
  shouldForwardProp: doNotForwardTransientProp(['disabled', 'readOnly'])
}).withConfig({
  displayName: "StyledAdornedInput",
  componentId: "c11n-8-64-0__sc-1kgphdl-0"
})(["", ""], AdornedInputMixin);
/**
 * Add flair to your [`Input`](#/Form%20components?id=input) components using the
 * [`Adornment`](#/Form%20components?id=adornment) component.
 *
 * @since 5.0.0
 */

var AdornedInput = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var disabled = props.disabled,
      error = props.error,
      input = props.input,
      inputId = props.inputId,
      leftAdornment = props.leftAdornment,
      readOnly = props.readOnly,
      rightAdornment = props.rightAdornment,
      size = props.size;
  var context = React.useContext(FormFieldContext);
  var defaultId = React.useRef(identifier()).current;
  var id = inputId || context.controlId || defaultId;
  var leftAdornmentId = React.useRef(identifier()).current;
  var rightAdornmentId = React.useRef(identifier()).current; // Generate the labelledby ids

  var labels = [];

  if (context.labelId) {
    labels.push(context.labelId);
  }

  if (leftAdornment) {
    labels.push(leftAdornmentId);
  }

  if (rightAdornment) {
    labels.push(rightAdornmentId);
  } // These props must be the same for both the input and the adornments. Don't allow consumers
  // to manually override these, i.e. make them set these on the top level AdornedInput.


  var coordinatedProps = deleteUndefined({
    size: size,
    error: typeof error !== 'undefined' ? error : context.error,
    disabled: typeof disabled !== 'undefined' ? disabled : context.disabled,
    readOnly: typeof readOnly !== 'undefined' ? readOnly : context.readOnly
  });
  return (
    /*#__PURE__*/
    // Note: Input component needs to be first in the DOM tree so that
    // Adornments can trigger off :hover/:focus styles.
    React.createElement(StyledAdornedInput, _extends({
      ref: ref
    }, props, coordinatedProps), input && /*#__PURE__*/React.cloneElement(input, Object.assign({
      id: id,
      // aria-labelledby is only necessary when there are multiple labels
      // https://dequeuniversity.com/rules/axe/3.3/form-field-multiple-labels
      'aria-labelledby': labels.length > 1 ? labels.join(' ') : undefined
    }, input.props, coordinatedProps)), leftAdornment && /*#__PURE__*/React.cloneElement(leftAdornment, Object.assign({
      htmlFor: id,
      id: leftAdornmentId
    }, leftAdornment.props, coordinatedProps, {
      position: 'left'
    })), rightAdornment && /*#__PURE__*/React.cloneElement(rightAdornment, Object.assign({
      htmlFor: id,
      id: rightAdornmentId
    }, rightAdornment.props, coordinatedProps, {
      position: 'right'
    })))
  );
});
AdornedInput.displayName = 'AdornedInput';
AdornedInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /** An adornment component. The `position` prop will automatically be set to `left`. */
  leftAdornment: CustomPropTypes.instanceOfComponent(Adornment),

  /** An adornment component. The `position` prop will automatically be set to `right`. */
  rightAdornment: CustomPropTypes.instanceOfComponent(Adornment),

  /** An [`Input`](#/Form%20components?id=input) component. */
  input: PropTypes.element.isRequired,

  /**
   * This will set the `id` of the nested [`Input`](#/Form%20components?id=input) component
   * as well as the `for` attribute for any nested adornment
   * components. If not provided, an id will be automatically generated.
   */
  inputId: PropTypes.string,

  /**
   * Set the error prop for any nested [`Input`](#/Form%20components?id=input) and
   * adornment components.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  error: PropTypes.bool,

  /**
   * Set the size prop for any nested [`Input`](#/Form%20components?id=input) and
   * adornment components.
   */
  size: PropTypes.string,

  /**
   * Set the disabled prop for any nested [`Input`](#/Form%20components?id=input) and
   * adornment components.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  disabled: PropTypes.bool,

  /**
   * Inputs are fluid by default, set this to `false` for width auto.
   */
  fluid: PropTypes.bool,

  /**
   * Set the readOnly prop for any nested [`Input`](#/Form%20components?id=input) and
   * adornment components.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   *
   * @since 8.27.0
   */
  readOnly: PropTypes.bool
} : {};
AdornedInput.defaultProps = {
  fluid: true
};
export default AdornedInput;